import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Group, Select, Stack } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { listOrganizations, listUsers, listVisits, type ListParams } from '../../api/resources';
import { preferApplicantSubject } from '../../lib/subjectUtils';
import { DataPanel } from '../../components/DataPanel';
import { EntityRef } from '../../components/EntityRef';
import { PageHeader } from '../../components/PageHeader';
import { CreateVisitModal } from '../../components/visits/CreateVisitModal';
import { PaginatedTable } from '../../components/PaginatedTable';
import { useAdminLookups } from '../../hooks/useAdminLookups';
import { DEFAULT_PAGE_SIZE } from '../../hooks/useListQuery';
import type { Visit } from '../../types/api';
import { VISIT_STATUS_VALUES } from '../../types/domain';

export function VisitsPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [orgFilter, setOrgFilter] = useState<string | null>(null);
    const [officerFilter, setOfficerFilter] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<string | null>(null);
    const [flaggedFilter, setFlaggedFilter] = useState<string | null>(null);
    const [createOpen, setCreateOpen] = useState(false);
    const lookups = useAdminLookups({ includeCases: true });

    const orgsQ = useQuery({
        queryKey: ['organizations-all'],
        queryFn: () => listOrganizations({ page: 1, limit: 500 }),
    });
    const usersQ = useQuery({
        queryKey: ['users-field-officers'],
        queryFn: () => listUsers({ page: 1, limit: 500 }),
    });

    const listParams = useMemo(
        (): ListParams => ({
            page,
            limit: DEFAULT_PAGE_SIZE,
            sort: JSON.stringify([{ field: 'createdAt', order: 'DESC' }]),
            // Monitoring visits live on their own page; exclude them here so
            // the two pipelines never overlap.
            excludeVisitType: 'monitoring',
            ...(orgFilter ? { organizationId: orgFilter } : {}),
            ...(officerFilter ? { fieldOfficerId: officerFilter } : {}),
            ...(statusFilter ? { status: statusFilter } : {}),
            ...(flaggedFilter ? { flagged: flaggedFilter } : {}),
        }),
        [page, orgFilter, officerFilter, statusFilter, flaggedFilter],
    );

    const visitsQ = useQuery({
        queryKey: ['visits', listParams],
        queryFn: () => listVisits(listParams),
    });

    const fieldOfficers = (usersQ.data?.data ?? []).filter((u) => !u.isPlatformAdmin);
    const orgOptions = (orgsQ.data?.data ?? []).map((o) => ({ value: o.id, label: o.name }));

    return (
        <Stack gap="lg">
            <PageHeader
                title={t('features.visits.title')}
                description={t('features.visits.description')}
                actions={
                    <Button onClick={() => setCreateOpen(true)}>
                        {t('features.visits.createAction')}
                    </Button>
                }
            />
            <DataPanel>
                <Group grow align="flex-end">
                    <Select
                        label={t('fields.organization')}
                        placeholder={t('common.all')}
                        clearable
                        searchable
                        data={orgOptions}
                        value={orgFilter}
                        onChange={(v) => {
                            setOrgFilter(v);
                            setPage(1);
                        }}
                    />
                    <Select
                        label={t('fields.fieldOfficer')}
                        placeholder={t('common.all')}
                        clearable
                        searchable
                        data={fieldOfficers.map((u) => ({
                            value: u.id,
                            label: `${u.firstName} ${u.lastName} (${u.email ?? u.id})`,
                        }))}
                        value={officerFilter}
                        onChange={(v) => {
                            setOfficerFilter(v);
                            setPage(1);
                        }}
                    />
                    <Select
                        label={t('fields.status')}
                        placeholder={t('common.all')}
                        clearable
                        data={VISIT_STATUS_VALUES.map((status) => ({
                            value: status,
                            label: t(`visitStatuses.${status}`),
                        }))}
                        value={statusFilter}
                        onChange={(v) => {
                            setStatusFilter(v);
                            setPage(1);
                        }}
                    />
                    <Select
                        label={t('features.visits.flaggedFilter')}
                        placeholder={t('common.all')}
                        clearable
                        data={[
                            { value: 'true', label: t('features.visits.flaggedOnly') },
                            { value: 'false', label: t('features.visits.notFlagged') },
                        ]}
                        value={flaggedFilter}
                        onChange={(v) => {
                            setFlaggedFilter(v);
                            setPage(1);
                        }}
                    />
                </Group>
            </DataPanel>
            <PaginatedTable
                rowKey={(r: Visit) => r.id}
                columns={[
                    {
                        key: 'case',
                        header: t('fields.case'),
                        render: (r) => {
                            const c = lookups.resolveCase(r.caseId, r.caseRecord);
                            return (
                                <EntityRef
                                    title={c.title}
                                    id={c.id}
                                    href={`/cases/${r.caseId}`}
                                />
                            );
                        },
                    },
                    {
                        key: 'subject',
                        header: t('fields.subject'),
                        render: (r) => {
                            const link = preferApplicantSubject(r.caseRecord?.caseSubjects);
                            const subject = link?.subject;
                            const subjectId = link?.subjectId;
                            if (!subjectId) {
                                return t('common.empty');
                            }
                            return (
                                <EntityRef
                                    title={subject?.fullName ?? t('entities.subject')}
                                    id={subjectId}
                                    href={`/subjects?id=${subjectId}`}
                                    hideId
                                />
                            );
                        },
                    },
                    {
                        key: 'poi',
                        header: t('fields.poi'),
                        render: (r) => {
                            const visitPoi = r.visitPois?.[0]?.poi;
                            const visitPoiId = r.visitPois?.[0]?.poiId;
                            const casePoi = r.caseRecord?.casePois?.[0]?.poi;
                            const casePoiId = r.caseRecord?.casePois?.[0]?.poiId;
                            const poiId = visitPoiId ?? casePoiId;
                            const poi = visitPoi ?? casePoi;
                            if (!poiId) {
                                return t('common.empty');
                            }
                            return (
                                <EntityRef
                                    title={poi?.name ?? t('entities.poi')}
                                    id={poiId}
                                    secondary={poi?.claimedAddress}
                                    href={`/pois?id=${poiId}`}
                                    hideId
                                />
                            );
                        },
                    },
                    {
                        key: 'type',
                        header: t('fields.visitType'),
                        render: (r) => r.visitType ?? t('common.empty'),
                    },
                    {
                        key: 'status',
                        header: t('fields.status'),
                        render: (r) => (
                            <Group gap="xs">
                                {r.status ? (
                                    <Badge variant="light" size="sm">
                                        {t(`visitStatuses.${r.status}`, { defaultValue: r.status })}
                                    </Badge>
                                ) : (
                                    t('common.empty')
                                )}
                                {r.data?.flagged ? (
                                    <Badge color="orange" size="sm" variant="filled">
                                        {t('features.visits.flaggedBadge')}
                                    </Badge>
                                ) : null}
                            </Group>
                        ),
                    },
                    {
                        key: 'officer',
                        header: t('fields.fieldOfficer'),
                        render: (r) => {
                            const u = lookups.resolveUser(r.fieldOfficerId, r.fieldOfficer);
                            return (
                                <EntityRef
                                    title={u.title}
                                    id={u.id}
                                    secondary={u.secondary}
                                    hideId={!r.fieldOfficerId}
                                />
                            );
                        },
                    },
                    {
                        key: 'visit',
                        header: t('fields.visit'),
                        render: (r) => {
                            const label = lookups.resolveVisit(r.id, r);
                            return (
                                <EntityRef
                                    title={label.title}
                                    id={r.id}
                                    href={`/visits/${r.id}`}
                                />
                            );
                        },
                    },
                ]}
                rows={visitsQ.data?.data ?? []}
                total={visitsQ.data?.total ?? 0}
                page={page}
                pageSize={DEFAULT_PAGE_SIZE}
                onPageChange={setPage}
                isLoading={visitsQ.isLoading}
                onRowClick={(r) => navigate(`/visits/${r.id}`)}
            />
            <CreateVisitModal
                opened={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={(visit) => navigate(`/visits/${visit.id}`)}
            />
        </Stack>
    );
}
